import React from 'react';
import { Tab } from '@headlessui/react';
import { classNames } from '@/utils/classNames';
import { Badge } from '../Badge';

export interface SubTab {
    name: string;
    count?: number;
    content: React.ReactNode;
}

interface SubTabsProps {
    tabs: SubTab[];
    selectedIndex?: number;
    onChange?: (index: number) => void;
    variant?: 'primary' | 'secondary';
}

/**
 * Componente SubTabs reutilizável para criar interfaces com abas
 * Substitui múltiplas implementações locais de Tab.Group
 */
const SubTabs: React.FC<SubTabsProps> = ({
    tabs,
    selectedIndex,
    onChange,
    variant = 'primary'
}) => {
    const colorScheme = {
        primary: {
            background: 'bg-blue-900/20',
            textColor: 'text-blue-700',
            selectedBg: 'bg-white shadow',
            hoverBg: 'text-blue-100 hover:bg-white/[0.12] hover:text-white'
        },
        secondary: {
            background: 'bg-gray-900/20',
            textColor: 'text-gray-700',
            selectedBg: 'bg-white shadow',
            hoverBg: 'text-gray-100 hover:bg-white/[0.12] hover:text-white'
        }
    };

    const colors = colorScheme[variant];

    return (
        <div className="w-full">
            <Tab.Group selectedIndex={selectedIndex} onChange={onChange}>
                <Tab.List className={`flex space-x-1 rounded-xl ${colors.background} p-1`}>
                    {tabs.map((tab) => (
                        <Tab
                            key={tab.name}
                            className={({ selected }) =>
                                classNames(
                                    `w-full rounded-lg py-2.5 text-sm font-medium leading-5 ${colors.textColor} flex items-center justify-center`,
                                    'ring-white ring-opacity-60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2',
                                    selected ? colors.selectedBg : colors.hoverBg
                                )
                            }
                        >
                            {tab.name}
                            {typeof tab.count === 'number' && (
                                <Badge count={tab.count} variant={variant} />
                            )}
                        </Tab>
                    ))}
                </Tab.List>
                <Tab.Panels className="mt-2">
                    {tabs.map((tab, idx) => (
                        <Tab.Panel
                            key={idx}
                            className={classNames(
                                'rounded-xl bg-white p-0.5',
                                'ring-white ring-opacity-60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2'
                            )}
                        >
                            {tab.content}
                        </Tab.Panel>
                    ))}
                </Tab.Panels>
            </Tab.Group>
        </div>
    );
};

export default SubTabs; 